/**
 * RFC 5322 threading helpers for the headers stored on `booking_emails`
 * (`rfc_message_id`, `rfc_references`).
 *
 * A mail client groups a conversation from three headers: `Message-ID` on every
 * message, `In-Reply-To` naming the parent, and `References` carrying the chain
 * back to the root. Gmail will also fall back to grouping by subject and
 * participants; Outlook will not. Replies sent without these headers arrive as a
 * brand new conversation, which is what clients were seeing from us.
 *
 * Note that none of this is the Gmail API's `threadId`. That groups a message
 * inside our own mailbox and means nothing to the recipient.
 */

/** A Message-ID as it appears in a header: angle-bracketed, no internal spaces. */
const MESSAGE_ID = /<[^<>\s]+>/g

/**
 * How many entries to keep in a References chain. Long chains get truncated by
 * intermediate servers anyway, and clients that trim keep the root plus the most
 * recent — the root is what most of them group on.
 */
const MAX_REFERENCES = 21

/** A leading "Re:", "RE :" or "Re[2]:" on a subject line. */
const REPLY_PREFIX = /^\s*re\s*(\[\d+\])?\s*:/i

/**
 * Pull the Message-IDs out of a header value. Accepts the space-separated string
 * form and the array form `mailparser` sometimes returns for `references`.
 */
export const parseMessageIds = (value: string | string[] | null | undefined): string[] => {
  if (!value) return []

  const text = Array.isArray(value) ? value.join(' ') : value

  return text.match(MESSAGE_ID) ?? []
}

/**
 * Coerce a stored or parsed value to a single angle-bracketed Message-ID, or null
 * when there is nothing usable. Values normally arrive bracketed already; this
 * covers rows where the brackets were stripped somewhere along the way.
 */
export const normaliseMessageId = (value: string | string[] | null | undefined): string | null => {
  const [first] = parseMessageIds(value)
  if (first) return first

  const text = (Array.isArray(value) ? value[0] : value)?.trim()
  if (!text || /[\s<>]/.test(text)) return null

  return `<${text}>`
}

/**
 * Build the `References` header for a reply: the parent's own References, then the
 * parent's Message-ID. That is the chain RFC 5322 §3.6.4 asks for.
 *
 * Returns an empty string when the parent has no Message-ID and no References —
 * there is nothing to thread against, and an empty header is worse than none.
 */
export const buildReferences = (
  parentReferences: string | string[] | null | undefined,
  parentMessageId: string | string[] | null | undefined
): string => {
  const chain = [...parseMessageIds(parentReferences), ...parseMessageIds(parentMessageId)]

  const seen = new Set<string>()
  const unique: string[] = []

  for (const messageId of chain) {
    if (seen.has(messageId)) continue

    seen.add(messageId)
    unique.push(messageId)
  }

  if (unique.length <= MAX_REFERENCES) return unique.join(' ')

  return [unique[0], ...unique.slice(unique.length - (MAX_REFERENCES - 1))].join(' ')
}

/**
 * Prefix a subject with "Re: " unless it already carries one. Not required for
 * threading — clients key on the headers above — but it is what people expect a
 * reply to look like, and it helps the subject fallback in clients that use one.
 */
export const withReplyPrefix = (subject: string | null | undefined): string => {
  const trimmed = (subject ?? '').trim()

  if (!trimmed) return 'Re:'

  return REPLY_PREFIX.test(trimmed) ? trimmed : `Re: ${trimmed}`
}
